import React, { ChangeEvent, FC, Fragment, useEffect, useState } from 'react';
import { Checkbox, TableCell, TableRow, Typography, Link, Tooltip, IconButton, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { InvoiceDelivery, ReviewOrders } from 'models/invoice';
import { formatCurrency, formatDate } from 'utils/format';
import { toggleOrderReadyForInvoiceTemplate } from 'apollo/templates/invoices/mutation.graphql';
import { useMutation } from '@apollo/client/react';
import { ApolloError } from '@apollo/client/errors';
import InvoiceItemNumberStatus from 'components/invoices/invoice-item/invoice-item-number-status/invoice-item-number-status.component';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoDisturbIcon from '@mui/icons-material/DoDisturb';
import { NavLink } from 'react-router-dom';
import OrderStatusChip from 'components/orders/shared/order-status-chip.component';
import { InvoicingState, OrderStatus } from 'models/order';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';

type OrderReviewItemProps = {
    reviewOrder: ReviewOrders;
    handleError: (error: ApolloError | null) => void;
    handleCreateInvoice: (reviewOrder: ReviewOrders) => void;
    handleToggleReadyForInvoice: (reviewOrder: ReviewOrders) => void;
    openMissingCustomerNumberDialog: () => void;
};
const OrderReviewItem: FC<OrderReviewItemProps> = ({
    reviewOrder,
    handleToggleReadyForInvoice,
    handleCreateInvoice,
    handleError,
    openMissingCustomerNumberDialog,
}: OrderReviewItemProps) => {
    const currentOrganization = currentOrgVar();
    const { t: translate } = useTranslation(['invoices', 'orders']);

    const [checkedReady, setCheckedReady] = useState(
        reviewOrder.invoicingState === InvoicingState.READY_FOR_INVOICE ||
            reviewOrder.invoicingState === InvoicingState.SHOULD_NOT_INVOICE,
    );
    const [invoiceType, setInvoiceType] = useState('');
    const [invoiceEmail, setInvoiceEmail] = useState('');
    const [invoiceDelivery, setInvoiceDelivery] = useState('');
    const [isPtjOrg, setIsPtjOrg] = useState(false);

    const setClientInvoiceSettings = () => {
        setInvoiceType(reviewOrder.clientInvoiceSettings?.invoiceType as string);
        setInvoiceDelivery(reviewOrder.clientInvoiceSettings?.invoiceDelivery as string);
        setInvoiceEmail(reviewOrder.clientInvoiceSettings?.invoiceEmail);
    };

    useEffect(() => {
        const isPtjOrg = customerSettings.getCustomerSetting(
            currentOrganization?.customerSettings,
            CONSTANTS.SETTINGS_NAMES.PTJ_ORGANIZATION,
        );
        setIsPtjOrg(isPtjOrg);
        if (reviewOrder) {
            const isChildclinic = reviewOrder.clinicId !== reviewOrder.parentClinicId;
            setCheckedReady(
                reviewOrder.invoicingState === InvoicingState.READY_FOR_INVOICE ||
                    reviewOrder.invoicingState === InvoicingState.SHOULD_NOT_INVOICE,
            );
            const labOverrideClinicForcedSetting = customerSettings.getCustomerSetting(
                currentOrganization.customerSettings || [],
                CONSTANTS.SETTINGS_NAMES.ALLOW_LAB_INVOICE_OVERRIDE,
            );
            if (
                labOverrideClinicForcedSetting &&
                reviewOrder.clinicInvoiceDeliveryMethod !== InvoiceDelivery.SCHEDULED_ELECTRONIC
            ) {
                setClientInvoiceSettings();
            } else {
                if (isChildclinic || !reviewOrder.clinicInvoiceType || !reviewOrder.clinicInvoiceDeliveryMethod) {
                    setClientInvoiceSettings();
                } else {
                    setInvoiceDelivery(reviewOrder.clinicInvoiceDeliveryMethod);
                    setInvoiceType(reviewOrder.clinicInvoiceType);
                }
            }
        }
    }, [reviewOrder]);

    const [toggleOrderReadyForInvoice] = useMutation(toggleOrderReadyForInvoiceTemplate, {
        onCompleted: ({ toggleOrderReadyForInvoice: reviewOrder }) => {
            handleToggleReadyForInvoice(reviewOrder);
        },
        onError: (error: ApolloError) => {
            handleError(error);
            setCheckedReady(!checkedReady);
            setTimeout(() => {
                handleError(null);
            }, 10000);
        },
    });

    const handleReadyForInvoiceCheckboxClick = (reviewOrder: ReviewOrders, invoiceAction: string) => {
        return toggleOrderReadyForInvoice({
            variables: {
                orderId: reviewOrder._id,
                invoiceAction: invoiceAction,
            },
        });
    };

    const allowReadyForInvoiceCheck = () => {
        if (!reviewOrder.invoicingState) {
            return true;
        }
        return (
            reviewOrder.invoicingState === InvoicingState.NOT_READY ||
            reviewOrder.invoicingState === InvoicingState.READY_FOR_INVOICE
        );
    };

    const handleCheckedReady = (event: ChangeEvent<HTMLInputElement>) => {
        if (!allowReadyForInvoiceCheck()) {
            event.preventDefault();
            return;
        }
        if (isPtjOrg && event.target.checked && !reviewOrder.ptjCustomerNumber) {
            // open dialog here
            openMissingCustomerNumberDialog();
            return;
        }
        setCheckedReady(event.target.checked);
        const invoiceAction = event.target.checked ? InvoicingState.READY_FOR_INVOICE : InvoicingState.NOT_READY;
        handleReadyForInvoiceCheckboxClick(reviewOrder, invoiceAction);
    };

    const createInvoice = () => {
        handleCreateInvoice({
            ...reviewOrder,
            invoiceDeliveryMethod: invoiceDelivery,
            clinicInvoiceEmail: reviewOrder.clinicInvoiceEmail || invoiceEmail,
        });
    };

    return (
        <Fragment>
            <TableRow
                sx={{
                    height: 73,
                }}
                selected={checkedReady}
                hover
            >
                <TableCell align="center">
                    {invoiceDelivery &&
                    invoiceType &&
                    (!reviewOrder.invoicingState || reviewOrder.invoicingState === InvoicingState.NOT_READY) &&
                    reviewOrder.totalAmount?.totalPrice !== 0 &&
                    !reviewOrder.invoiceStatus &&
                    invoiceType === 'SINGLE' ? (
                        <IconButton onClick={createInvoice}>
                            <NoteAddOutlinedIcon color="info" />
                        </IconButton>
                    ) : reviewOrder.invoicingState === InvoicingState.SHOULD_NOT_INVOICE ? (
                        <Tooltip title={translate('DO_NOT_INVOICE').toString()}>
                            <DoDisturbIcon color="success" />
                        </Tooltip>
                    ) : reviewOrder.totalAmount?.totalPrice === 0 ? (
                        <Tooltip title={translate('orders:ZERO_PRICE_ORDER').toString()}>
                            <DoDisturbIcon color="disabled" />
                        </Tooltip>
                    ) : allowReadyForInvoiceCheck() ? (
                        <Checkbox color="secondary" checked={checkedReady} onChange={handleCheckedReady} />
                    ) : (
                        <Tooltip title={translate('ORDER_HAS_BEEN_INVOICED').toString()}>
                            <CheckCircleIcon color="success" />
                        </Tooltip>
                    )}
                </TableCell>
                <TableCell sx={{ minWidth: 150, width: 300 }}>{reviewOrder.clinicName}</TableCell>
                <TableCell>
                    <Box>
                        <Link component={NavLink} to={`/order-details/${reviewOrder._id}/main-view`}>
                            {reviewOrder.orderId}
                        </Link>
                    </Box>
                    <Box
                        sx={{
                            whiteSpace: 'nowrap',
                        }}
                    >
                        <OrderStatusChip status={reviewOrder.orderStatus.toUpperCase() as OrderStatus} />
                    </Box>
                </TableCell>
                <TableCell>
                    {reviewOrder.deliveryDate ? (
                        <Box>{formatDate(reviewOrder.deliveryDate, 'date')}</Box>
                    ) : (
                        <Box
                            sx={{
                                whiteSpace: 'nowrap',
                                color: '#888',
                            }}
                        >
                            {translate('NOT_DELIVERED')}
                        </Box>
                    )}
                </TableCell>
                <TableCell>
                    {reviewOrder.invoiceNumber ? (
                        <Box>{translate(`${reviewOrder.invoiceType || 'SINGLE'}`)}</Box>
                    ) : (
                        <Box
                            sx={{
                                whiteSpace: 'nowrap',
                                color: '#888',
                            }}
                        >
                            {translate('NOT_INVOICED')}
                        </Box>
                    )}
                </TableCell>
                <TableCell>
                    {reviewOrder.invoiceNumber ? (
                        <InvoiceItemNumberStatus
                            pdfUrl={reviewOrder.pdfUrl}
                            invoiceNumber={reviewOrder.invoiceNumber}
                            status={reviewOrder.invoiceStatus}
                            invoiceType={reviewOrder.invoiceType}
                        />
                    ) : (
                        <Box
                            sx={{
                                whiteSpace: 'nowrap',
                                color: '#888',
                            }}
                        >
                            <Box>{reviewOrder.tempInvoiceNumber}</Box>
                            <Box>{translate('NOT_INVOICED')}</Box>
                        </Box>
                    )}
                </TableCell>
                <TableCell>{formatDate(reviewOrder.orderCreatedDate, 'date')}</TableCell>
                <TableCell>
                    {reviewOrder.invoiceSentDate ? (
                        <Box>{formatDate(reviewOrder.invoiceSentDate, 'date')}</Box>
                    ) : (
                        <Box
                            sx={{
                                whiteSpace: 'nowrap',
                                color: '#888',
                            }}
                        >
                            {reviewOrder.invoiceNumber ? translate('NOT_SENT') : translate('NOT_INVOICED')}
                        </Box>
                    )}
                </TableCell>
                <TableCell align="right">
                    <Box>
                        {formatCurrency(
                            (reviewOrder.totalAmount?.totalPrice || 0) + (reviewOrder.totalAmount?.vatAmount || 0),
                            reviewOrder.currency,
                        )}
                    </Box>

                    <Box
                        sx={{
                            whiteSpace: 'nowrap',
                        }}
                    >
                        <Typography variant="caption">
                            {translate('orders:VAT')}:{' '}
                            {formatCurrency(reviewOrder.totalAmount?.vatAmount || 0, reviewOrder.currency)}
                        </Typography>
                    </Box>
                </TableCell>
            </TableRow>
        </Fragment>
    );
};

export default OrderReviewItem;
